import React, { useEffect, useState } from "react";
import {
  Datagrid,
  TextField,
  FunctionField,
  EditButton,
  useListController,
  ListContextProvider,
  Loading,
  useListContext,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/firebase";
import { collection, onSnapshot, } from "firebase/firestore";
import VehicleExcelExport from "./VehicleExcelExport";
import VehiclePDFExport from "./VehiclePDFExport";
import { calculateRemainingFinance, isVehicleActiveAsOf, vehicleStatusAsOf, getFinalInstallmentEnd, formatDeferredInstallments } from "./vehicleFinance";


/* -----------------------------------
   🔹 Expiry cell with View link
----------------------------------- */
const ExpiryCellWithView = ({ record, dateSource, fileSource }) => {
  if (!record) return null;
  const expiry = record[dateSource];
  const fileUrl = record[fileSource];
  let color = "#666";
  let label = "-";

  if (expiry) {
    const date = expiry.seconds ? new Date(expiry.seconds * 1000) : new Date(expiry);
    const diffDays = (date - new Date()) / (1000 * 60 * 60 * 24);
    label = date.toLocaleDateString("en-GB");
    if (diffDays < 0) color = "red";
    else if (diffDays <= 30) color = "#e69500";
    else color = "green";
  }

  return (
    <div style={{ textAlign: "center" }}>
      <span style={{ color, fontWeight: 600 }}>{label}</span>
      {fileUrl && (
        <a
          href={fileUrl.startsWith("http") ? fileUrl : `https://${fileUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "block",
            fontSize: "12px",
            color: "#1976d2",
            textDecoration: "none",
            marginTop: 2,
          }}
        >
          View
        </a>
      )}
    </div>
  );
};

// ✅ Serial number field (matches current sorting/filtering)
const SerialField = ({ record }) => {
  const { ids } = useListContext();
  if (!record) return null;
  const idx = ids?.indexOf(record.id);
  return <span>{idx >= 0 ? idx + 1 : ""}</span>;
};


/* -----------------------------------
   🔹 Helper Functions
----------------------------------- */
const formatCurrency = (value) =>
  (Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getOrdinalSuffix = (n) => {
  if (n > 3 && n < 21) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const vehicleNumberSortValue = (value) => {
  const text = String(value || "").trim();
  const match = text.match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

const sortByVehicleNumber = (a, b) => {
  const aNo = vehicleNumberSortValue(a?.vehicleNumber);
  const bNo = vehicleNumberSortValue(b?.vehicleNumber);

  if (aNo !== bNo) return aNo - bNo;

  return String(a?.vehicleNumber || "").localeCompare(
    String(b?.vehicleNumber || ""),
    undefined,
    { numeric: true, sensitivity: "base" }
  );
};

const displayAsCashAsOf = (vehicle, asOf) => {
  const status = vehicleStatusAsOf(vehicle, asOf);
  return ["CASH", "PAID", "SETTLED"].includes(status);
};



/* -----------------------------------
   🔹 Vehicle List
----------------------------------- */
export const VehicleList = () => {
  const controller = useListController({ resource: "vehicles" });
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [includeSold, setIncludeSold] = useState(false);


  const role = localStorage.getItem("role")?.toLowerCase() || "";
  const canCreate = ["admin", "md"].includes(role);

  // 🔹 Load companies
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((d) => d.data().name).sort();
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  if (!controller || controller.isLoading) return <Loading />;

  const allData = Array.isArray(controller.data)
    ? controller.data
    : Object.values(controller.data || {});

  const reportAsOf = new Date(reportDate + "T00:00:00");

const filteredCompany =
  selectedCompany === "All"
    ? allData
    : allData.filter((v) => v.company === selectedCompany);

const filtered = filteredCompany.filter((v) => {
  // Must exist before report date
  const created = v.createdAt?.seconds
    ? new Date(v.createdAt.seconds * 1000)
    : v.createdAt
    ? new Date(v.createdAt)
    : null;

  if (created && created > reportAsOf) return false;

  const active = isVehicleActiveAsOf(v, reportAsOf);
  if (active) return true;

  return includeSold; // include sold/inactive (but only if existed by date)
}).sort(sortByVehicleNumber);


  /* -----------------------------------
   🔹 Group by Bank → Installment Date
----------------------------------- */

const groups = {};

filtered.forEach((v) => {
  
  const status = vehicleStatusAsOf(v, reportAsOf);
if (status === "CASH" || status === "PAID" || status === "SETTLED" || status === "SOLD") return;


  const bank = v.installmentBank || "Unknown Bank";
  const monthly = Number(v.monthlyInstallment) || 0;

  // ❌ Skip cars that have no remaining balance
  const remaining = calculateRemainingFinance(v, new Date(reportDate + "T00:00:00"));
  if (remaining <= 0) return;

  // 🔹 Extract installment date (deduction date)
  let installmentDay = 1;

  if (v.installmentStart) {
    const startDate = v.installmentStart.seconds
      ? new Date(v.installmentStart.seconds * 1000)
      : new Date(v.installmentStart);

    if (!isNaN(startDate.getTime())) {
      installmentDay = startDate.getDate();
    }
  }

  // 🔹 Build grouping structure
  if (!groups[bank]) groups[bank] = {};
  if (!groups[bank][installmentDay]) groups[bank][installmentDay] = 0;

  groups[bank][installmentDay] += monthly;
});

const sortedBanks = Object.keys(groups).sort((a, b) =>
  a.localeCompare(b, undefined, { sensitivity: "base" })
);

const grandTotal = sortedBanks.reduce((total, bank) => {
  const bankSum = Object.values(groups[bank]).reduce((a, b) => a + b, 0);
  return total + bankSum;
}, 0);


  /* -----------------------------------
     🔹 Render
  ----------------------------------- */
  const filteredIds = filtered.map((v) => v.id);

return (
  <ListContextProvider value={{ ...controller, data: filtered, ids: filteredIds }}>

      <div style={{ padding: 20 }}>
        {/* Header */}
        <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  }}
>
  <h2 style={{ margin: 0 }}>Vehicles</h2>

  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <VehicleExcelExport vehicles={filtered} asOfDate={reportDate} />
    <VehiclePDFExport vehicles={filtered} asOfDate={reportDate} />

    {canCreate && (
      <button
        onClick={() => navigate("/vehicles/create")}
        style={{
          background: "#1976d2",
          color: "white",
          border: "none",
          padding: "8px 16px",
          borderRadius: "6px",
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        ➕ Add Vehicle
      </button>
    )}
  </div>
</div>


        {/* Expiry Legend */}
        <div style={{ display: "flex", gap: "20px", marginBottom: 10 }}>
          <strong>Expiry Legend:</strong>
          <span style={{ color: "green" }}>🟩 Valid</span>
          <span style={{ color: "#e69500" }}>🟧 Expiring ≤30 days</span>
          <span style={{ color: "red" }}>🟥 Expired</span>
        </div>

        {/* Company Filter */}
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          style={{
            marginBottom: 15,
            padding: 6,
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        >
          <option value="All">All Companies</option>
          {companies.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>

        {/* Report As-Of Date (for history) */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 15, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong>Report As Of:</strong>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              style={{ padding: 6, borderRadius: 6, border: "1px solid #ccc" }}
            />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={includeSold}
              onChange={(e) => setIncludeSold(e.target.checked)}
            />
            Include sold vehicles
          </label>
        </div>

        {/* Vehicle Table */}
        <Datagrid
  rowClick={false}
  bulkActionButtons={false}
  rowStyle={(record) => {
    const asOf = new Date(reportDate + "T00:00:00");
    const status = vehicleStatusAsOf(record, asOf);
    if (status === "SOLD") return { backgroundColor: "#f3f3f3" };
    if (status === "CASH" || status === "PAID" || status === "SETTLED") return { backgroundColor: "#d1ffd6" };
    if (status === "DEFERRED") return { backgroundColor: "#fff7db" };
    return {};
  }}
>
        <FunctionField label="#" render={(record) => <SerialField record={record} />} />
          <TextField source="vehicleNumber" label="Vehicle Number" />
          <TextField source="vehicleType" label="Type / Model" />
          <TextField source="vehicleYear" label="Year" />

          <FunctionField
            label="Insurance"
            render={(r) => (
              <ExpiryCellWithView
                record={r}
                dateSource="insuranceExpiry"
                fileSource="insuranceFile"
              />
            )}
          />
          <FunctionField
            label="Registration"
            render={(r) => (
              <ExpiryCellWithView
                record={r}
                dateSource="registrationExpiry"
                fileSource="registrationFile"
              />
            )}
          />
          <FunctionField
            label="Mortgage From"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              return displayAsCashAsOf(r, asOf) ? "-" : r.mortgageFrom || "-";
            }}
          />
          <FunctionField
            label="Status"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              const status = vehicleStatusAsOf(r, asOf);
              return displayAsCashAsOf(r, asOf) ? "Cash" : status;
            }}
          />
          <FunctionField
            label="Sold Date"
            render={(r) => {
              if (!r.soldAt) return "-";
              const d = r.soldAt.seconds ? new Date(r.soldAt.seconds * 1000) : new Date(r.soldAt);
              return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
            }}
          />
          <FunctionField
            label="Installment Bank"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              return displayAsCashAsOf(r, asOf) ? "-" : r.installmentBank || "-";
            }}
          />
          <FunctionField
            label="Monthly Installment (AED)"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              return displayAsCashAsOf(r, asOf)
                ? "-"
                : `AED ${formatCurrency(r.monthlyInstallment)}`;
            }}
          />

          {/* 🔹 Newly Added Start/End Dates */}
          <FunctionField
            label="Installment Start"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              if (displayAsCashAsOf(r, asOf) || !r.installmentStart) return "-";
              return new Date(
                r.installmentStart.seconds
                  ? r.installmentStart.seconds * 1000
                  : r.installmentStart
              ).toLocaleDateString("en-GB");
            }}
          />
          <FunctionField
            label="Original End"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              if (displayAsCashAsOf(r, asOf) || !r.installmentEnd) return "-";
              return new Date(
                r.installmentEnd.seconds
                  ? r.installmentEnd.seconds * 1000
                  : r.installmentEnd
              ).toLocaleDateString("en-GB");
            }}
          />
          <FunctionField
            label="Final End"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              if (displayAsCashAsOf(r, asOf)) return "-";
              const d = getFinalInstallmentEnd(r);
              return d && !isNaN(d.getTime()) ? d.toLocaleDateString("en-GB") : "-";
            }}
          />
          <FunctionField
            label="Deferred Months"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              return displayAsCashAsOf(r, asOf) ? "-" : formatDeferredInstallments(r) || "-";
            }}
          />

          {/* 🔹 Live Remaining Balance */}
          <FunctionField
            label="Remaining Finance (AED)"
            render={(r) => {
              const asOf = new Date(reportDate + "T00:00:00");
              const remaining = displayAsCashAsOf(r, asOf) ? 0 : calculateRemainingFinance(r, asOf);
              return `AED ${formatCurrency(remaining)}`;
            }}
          /><EditButton />
        </Datagrid>

        {/* 💰 Finance Summary Box */}
        <div
          style={{
            marginTop: 40,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
            padding: "25px 35px",
            maxWidth: 950,
            margin: "40px auto",
          }}
        >
          {/* 🚗💰 Remaining Finance Summary (Company-wise) */}
<div
  style={{
    marginTop: 25,
    background: "#f8f9fa",
    border: "1px solid #dcdcdc",
    borderRadius: "10px",
    padding: "20px",
    maxWidth: 900,
    margin: "25px auto",
  }}
>
  <h3 style={{ marginBottom: 15, color: "#222" }}>
    🔎 Remaining Finance Summary
  </h3>

  {(() => {
    const companyTotals = {};
    let overall = 0;

    const asOf = new Date(reportDate + "T00:00:00");

    filtered.forEach((v) => {
      const status = vehicleStatusAsOf(v, asOf);
      const rem = ["FINANCED", "DEFERRED"].includes(status) ? calculateRemainingFinance(v, asOf) : 0;

      const company = v.company || "Unknown";
      if (!companyTotals[company]) companyTotals[company] = 0;

      companyTotals[company] += rem;
      overall += rem;
    });

    return (
      <>
        {Object.keys(companyTotals).map((co) => (
          <div
            key={co}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "6px 0",
              fontSize: "15px",
            }}
          >
            <strong>{co}:</strong>
            <span style={{ color: "#1565c0", fontWeight: 600 }}>
              AED {formatCurrency(companyTotals[co])}
            </span>
          </div>
        ))}

        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid #ccc",
            fontSize: "16px",
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "700",
          }}
        >
          <span>Grand Total:</span>
          <span style={{ color: "#2e7d32" }}>
            AED {formatCurrency(overall)}
          </span>
        </div>
      </>
    );
  })()}
</div>

<h3
  style={{
    marginBottom: 20,
    color: "#222",
    fontWeight: 700,
    fontSize: "20px",
  }}
>
  💰 Vehicle Installment Summary{" "}
  <span style={{ color: "#888", fontWeight: 500 }}>(Excluding Cash)</span>
</h3>


          {sortedBanks.length === 0 ? (
            <p style={{ color: "#777", marginBottom: 0 }}>
              No financed vehicles found.
            </p>
          ) : (
            sortedBanks.map((bank) => {
              const dateMap = groups[bank];
              const bankTotal = Object.values(dateMap).reduce((a, b) => a + b, 0);
              const sortedDays = Object.keys(dateMap)
                .map(Number)
                .sort((a, b) => a - b);

              return (
                <div
                  key={bank}
                  style={{
                    marginBottom: 25,
                    borderBottom: "1px dashed #ddd",
                    paddingBottom: 10,
                  }}
                >
                  <h4
                    style={{
                      marginBottom: 8,
                      color: "#1565c0",
                      fontWeight: 700,
                      fontSize: "17px",
                    }}
                  >
                    🏦 {bank}
                  </h4>

                  <ul style={{ margin: 0, paddingLeft: 22, listStyle: "none" }}>
                    {sortedDays.map((day) => (
                      <li
                        key={day}
                        style={{
                          color: "#444",
                          marginBottom: 5,
                          fontSize: "15px",
                        }}
                      >
                        •{" "}
                        <span style={{ color: "#000" }}>
                          {day}
                          {getOrdinalSuffix(day)} of each month
                        </span>{" "}
                        →{" "}
                        <strong style={{ color: "#1b5e20" }}>
                          AED {formatCurrency(dateMap[day])}
                        </strong>
                      </li>
                    ))}
                  </ul>

                  <div
                    style={{
                      marginTop: 6,
                      fontWeight: 600,
                      color: "#000",
                      textAlign: "right",
                      fontSize: "15px",
                    }}
                  >
                    ➤ Bank Total:{" "}
                    <span style={{ color: "#2e7d32" }}>
                      AED {formatCurrency(bankTotal)}
                    </span>
                  </div>
                </div>
              );
            })
          )}

          <div
            style={{
              marginTop: 20,
              paddingTop: 10,
              borderTop: "2px solid #ddd",
              textAlign: "right",
              fontWeight: 700,
              fontSize: "17px",
              color: "#111",
            }}
          >
            💵 Grand Total (All Banks):{" "}
            <span style={{ color: "#2e7d32" }}>
              AED {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>
    </ListContextProvider>
  );
};
